import React, { Suspense, useMemo, useRef, useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // Adicionado

import { isPublicRoute } from '../PublicRoutes';
import { Spin } from 'components/Spin';
import { useUser } from 'global/UserContext';
import { useAppInitialization } from 'hooks/useAppInitialization';
import { useMenuRoutes } from 'hooks/useMenuRoutes';
import { useNavigationSync } from 'hooks/useNavigationSync';
import { lazyLoad } from 'utils/lazyLoad';

// Lazy load Error page - only needed when route is not found
const Error = lazyLoad(() => import('pages/Errors/Error'));

// System routes that are not in the menu but should be accessible
const SYSTEM_ROUTES = new Set(['/settings/profile']);

interface RouteGuardProps {
  children: React.ReactNode;
}

/**
 * Component that manages all route access logic
 * - Authentication control (token)
 * - Route validation (404)
 * - Redirections (login, first valid route)
 * - Prevents 404 flashes during initialization
 *
 * Rendering flow:
 * 1. Public route? → Render immediately
 * 2. Still loading (user/menu)? → Show spinner
 * 3. No token? → Show spinner (redirect happens in useEffect)
 * 4. Root path (/)? → Show spinner (redirect effect handles navigation)
 * 5. Not fully initialized? → Show spinner (wait for menu selection)
 * 6. Route invalid? → Show 404
 * 7. All good → Render content
 */
const RouteGuard: React.FC<RouteGuardProps> = ({ children }) => {
  const location = useLocation();
  const { token } = useUser();
  const { isFullyInitialized, loadingStates } = useAppInitialization();
  const menuRoutes = useMenuRoutes();
  const { navigateToPath, restoreNavigation } = useNavigationSync();
  const { t } = useTranslation('common'); // Adicionado

  // Check if basic loading has completed (regardless of whether there is data or not)
  const isLoadingComplete = !loadingStates.userLoading && !loadingStates.menuLoading;

  // Cache of valid routes to avoid unnecessary validations
  const [validRoutesCache, setValidRoutesCache] = useState<Set<string>>(new Set());
  const hasAttemptedRedirect = useRef(false);
  const lastValidatedPath = useRef<string>('');

  // Check if it is a public route
  const isPublicRouteCheck = useMemo(() => {
    return isPublicRoute(location.pathname as string);
  }, [location.pathname]);

  // Update valid routes cache when menuRoutes changes
  useEffect(() => {
    if (menuRoutes.length > 0) {
      const routes = new Set(menuRoutes.map(route => route.path));
      setValidRoutesCache(routes);
    }
  }, [menuRoutes]);

  // Check if the current route is valid using cache
  const isRouteValid = useMemo(() => {
    // Decode and trim URL path for comparison (defensive against Strapi trailing whitespace)
    const currentPath = decodeURIComponent(location.pathname).trim();

    // If it is a public route, it is always valid
    if (isPublicRouteCheck) {
      return true;
    }

    // If it is a system route (not in the menu but valid), it is always valid
    if (SYSTEM_ROUTES.has(currentPath)) {
      return true;
    }

    // During initialization, assume it is valid to avoid 404
    if (!isFullyInitialized) {
      return true;
    }

    // If there is no cache yet, temporarily assume valid
    if (validRoutesCache.size === 0) {
      return true;
    }

    // Check in cache
    return validRoutesCache.has(currentPath);
  }, [location.pathname, isPublicRouteCheck, isFullyInitialized, validRoutesCache]);

  // EXPLICIT HANDLING OF ROOT ROUTE (/)
  // Does NOT depend on isFullyInitialized — only needs menuRoutes to redirect.
  // This avoids a deadlock where MenuSelectionContext can't initialize on "/"
  // but RouteGuard waits for full initialization before redirecting away from "/".
  useEffect(() => {
    if (location.pathname !== '/') return;

    if (!token) {
      navigateToPath('/login');
      return;
    }

    // Only need menu routes loaded — not full initialization
    if (menuRoutes.length === 0) return;

    // Authenticated with menu loaded → restore last route or go to first menu
    const restored = restoreNavigation();
    if (!restored) {
      navigateToPath(menuRoutes[0].path, menuRoutes[0].documentId);
    }
  }, [location.pathname, token, menuRoutes, navigateToPath, restoreNavigation]);

  // AUTHENTICATION CONTROL AND REDIRECTIONS
  useEffect(() => {
    // Wait for basic loading to complete (does not need data, just needs to finish loading)
    if (!isLoadingComplete) {
      return;
    }

    // Decode and trim URL path for comparison (defensive against Strapi trailing whitespace)
    const currentPath = decodeURIComponent(location.pathname).trim();

    // If the route changed, reset the redirect flag
    if (currentPath !== lastValidatedPath.current) {
      hasAttemptedRedirect.current = false;
      lastValidatedPath.current = currentPath;
    }

    // Prevent multiple redirects
    if (hasAttemptedRedirect.current) {
      return;
    }

    // CASE 1: Unauthenticated user trying to access a protected route
    if (!token && !isPublicRouteCheck) {
      hasAttemptedRedirect.current = true;
      navigateToPath('/login');
      return;
    }

    // CASE 2: Authenticated user on a public route (except logout, event-invite, and embedded forms)
    // Only redirect if FULLY initialized (with menu loaded)
    const isEventInvitePath = currentPath.startsWith('/invite/event/');
    const isEmbeddedFormPath = currentPath.startsWith('/form/');
    if (
      token &&
      isPublicRouteCheck &&
      currentPath !== '/logout' &&
      !isEventInvitePath &&
      !isEmbeddedFormPath &&
      isFullyInitialized
    ) {
      hasAttemptedRedirect.current = true;

      // Try to restore previous navigation
      const restored = restoreNavigation();
      if (!restored) {
        // If unable to restore, go to the first valid route
        const firstValidRoute = menuRoutes[0];
        if (firstValidRoute?.path) {
          navigateToPath(firstValidRoute.path, firstValidRoute.documentId);
        }
      }
      return;
    }

    // CASE 3: Invalid route (not public, not a system route, and not in cache)
    // Only validate after full initialization to avoid false positives
    if (
      !isPublicRouteCheck &&
      !SYSTEM_ROUTES.has(currentPath) &&
      isFullyInitialized &&
      validRoutesCache.size > 0 &&
      !validRoutesCache.has(currentPath)
    ) {
      hasAttemptedRedirect.current = true;

      // Try to restore or go to the first valid route
      const restored = restoreNavigation();
      if (!restored) {
        const firstValidRoute = menuRoutes[0];
        if (firstValidRoute?.path) {
          navigateToPath(firstValidRoute.path, firstValidRoute.documentId);
        }
      }
      return;
    }
  }, [
    isLoadingComplete,
    isFullyInitialized,
    location.pathname,
    token,
    isPublicRouteCheck,
    validRoutesCache,
    menuRoutes,
    navigateToPath,
    restoreNavigation,
  ]);

  // CONDITIONAL RENDERING

  // 1. If it is a public route, render immediately (no full initialization needed)
  if (isPublicRouteCheck) {
    return <>{children}</>;
  }

  // 2. If still loading (user/menu), show loading
  if (!isLoadingComplete) {
    return (
      <div className="flex justify-center items-center h-screen w-full">
        <Spin />
      </div>
    );
  }

  // 3. If there is no token (on a protected route), show loading (redirect in progress)
  if (!token) {
    return (
      <div className="flex justify-center items-center h-screen w-full">
        <Spin />
      </div>
    );
  }

  // 4. Root path: show spinner while redirect effect handles navigation.
  // This is separate from full initialization to avoid the deadlock where
  // MenuSelectionContext can't init on "/" but we wait for it before redirecting.
  if (location.pathname === '/') {
    return (
      <div className="flex justify-center items-center h-screen w-full">
        <Spin />
      </div>
    );
  }

  // 5. For authenticated routes, wait for full initialization (menu loaded)
  if (!isFullyInitialized) {
    return (
      <div className="flex justify-center items-center h-screen w-full">
        <Spin />
      </div>
    );
  }

  // 6. Validate if the route exists
  if (!isRouteValid) {
    return (
      <Suspense
        fallback={
          <div className="flex justify-center items-center h-screen w-full">
            <Spin />
          </div>
        }
      >
        <Error
          title={t('errors.notFoundPage.title')}
          error="404"
          message={t('errors.notFoundPage.message')}
        />
      </Suspense>
    );
  }

  // 7. Valid and authenticated route, render normally
  return <>{children}</>;
};

export default RouteGuard;
